import { useState, useEffect } from "react";
import { X, Bell } from "lucide-react";
import {
  isNotificationSupported,
  isNotificationEnabled,
  enableNotifications,
  getStoredFCMToken,
} from "../services/fcmService";
import toast from "react-hot-toast";

function NotificationPrompt() {
  const [isVisible, setIsVisible] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);

  useEffect(() => {
    // Check if we should show the prompt
    const checkNotificationStatus = () => {
      // Check if notifications are supported
      const supported = isNotificationSupported();
      if (!supported) {
        return;
      }

      // Check if already enabled
      const enabled = isNotificationEnabled() && !!getStoredFCMToken();
      if (enabled) {
        return;
      }

      // Check if user has dismissed the prompt recently
      const dismissedUntil = localStorage.getItem("notification_prompt_dismissed_until");
      if (dismissedUntil) {
        const dismissedTime = parseInt(dismissedUntil);
        const now = Date.now();
        // Don't show again for 7 days after dismissal
        if (now < dismissedTime) {
          return;
        }
      }

      // Check if we've shown the prompt before
      const promptShownCount = parseInt(localStorage.getItem("notification_prompt_shown_count") || "0");
      
      // Don't show more than 3 times total if user keeps dismissing
      if (promptShownCount >= 3) {
        return;
      }

      // Show the prompt after a short delay (for better UX)
      setTimeout(() => {
        setIsVisible(true);
        localStorage.setItem("notification_prompt_shown_count", String(promptShownCount + 1));
      }, 2000);
    };

    checkNotificationStatus();
  }, []);

  const handleEnable = async () => {
    setIsEnabling(true);
    try {
      await enableNotifications();
      toast.success("Push notifications enabled!");
      setIsVisible(false);
      // Clear dismissal tracking since user enabled it
      localStorage.removeItem("notification_prompt_dismissed_until");
      localStorage.removeItem("notification_prompt_shown_count");
    } catch (error) {
      console.error("Failed to enable notifications:", error);
      toast.error(error.message || "Failed to enable notifications");
    } finally {
      setIsEnabling(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Don't show again for 7 days
    const sevenDaysFromNow = Date.now() + 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem("notification_prompt_dismissed_until", String(sevenDaysFromNow));
  };

  const handleLater = () => {
    setIsVisible(false);
    // Show again after 1 day
    const oneDayFromNow = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem("notification_prompt_dismissed_until", String(oneDayFromNow));
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fadeIn"
        onClick={handleLater}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6 pointer-events-none">
        <div
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full p-6 pointer-events-auto animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleLater}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
              <Bell className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Enable Notifications?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
              Get real-time updates about your queries, messages, and important notifications instantly.
            </p>
          </div>

          {/* Benefits List */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 dark:text-blue-400 text-xs">✓</span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Instant updates on query responses
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 dark:text-blue-400 text-xs">✓</span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Never miss important messages
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 dark:text-blue-400 text-xs">✓</span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Stay connected even when app is closed
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleEnable}
              disabled={isEnabling}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Bell className="w-5 h-5" />
              {isEnabling ? "Enabling..." : "Enable Notifications"}
            </button>

            <div className="flex gap-2">
              <button
                onClick={handleLater}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-colors font-medium text-sm"
              >
                Maybe Later
              </button>
              <button
                onClick={handleDismiss}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-colors font-medium text-sm"
              >
                Don't Ask Again
              </button>
            </div>
          </div>

          {/* Small note */}
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
            You can change this anytime in your Profile settings
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
}

export default NotificationPrompt;
